<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { getProvenance, getRegister } from "../api";
import BitfieldDiagram from "../components/BitfieldDiagram.vue";
import EntityHeader from "../components/EntityHeader.vue";
import ProvenanceList from "../components/ProvenanceList.vue";
import type { ProvenanceItem, Register } from "../types";
const route = useRoute();
const register = ref<Register>();
const provenance = ref<ProvenanceItem[]>([]);
const release = computed(() => String(route.params.release));
async function load() {
  const revision = typeof route.query.revision === "string" ? route.query.revision : undefined;
  register.value = await getRegister(release.value, String(route.params.entity), revision);
  provenance.value = await getProvenance(
    release.value,
    register.value.peripheral_entity_id,
    register.value.revision_id,
  );
}
onMounted(() => void load());
watch(
  () => route.fullPath,
  () => void load(),
);
</script>
<template>
  <div v-if="register" class="page-shell">
    <nav class="breadcrumbs">
      <RouterLink to="/">Catalog</RouterLink><span>/</span
      ><RouterLink
        :to="{
          path: `/releases/${release}/peripherals/${register.peripheral_entity_id}`,
          query: { revision: register.revision_id },
        }"
        >{{ register.peripheral_name }}</RouterLink
      ><span>/</span><strong>{{ register.name }}</strong>
    </nav>
    <EntityHeader
      eyebrow="Register"
      :title="register.name"
      :description="register.description"
      :revision="register.revision_id"
    />
    <div class="fact-strip">
      <div>
        <span>Offset</span
        ><strong
          ><code>{{ register.offset }}</code></strong
        >
      </div>
      <div>
        <span>Width</span><strong>{{ register.width }} bits</strong>
      </div>
      <div>
        <span>Access</span
        ><strong
          >{{ register.access.readable ? "Read" : ""
          }}{{ register.access.writable ? " / Write" : "" }}</strong
        >
      </div>
      <div>
        <span>Reset</span
        ><strong
          ><code>{{ register.reset_value ?? "Unknown" }}</code></strong
        >
      </div>
    </div>
    <section class="section-block">
      <div class="section-heading">
        <div>
          <p class="eyebrow">Physical layout</p>
          <h2>Bitfields</h2>
        </div>
      </div>
      <BitfieldDiagram :fields="register.fields" :width="register.width" />
      <div class="field-list">
        <article v-for="field in register.fields" :key="field.name">
          <div>
            <strong>{{ field.name }}</strong
            ><code>[{{ field.lsb + field.width - 1 }}:{{ field.lsb }}]</code>
          </div>
          <p>{{ field.description }}</p>
          <span
            >{{ field.access.readable ? "read" : "" }} {{ field.access.writable ? "write" : "" }}
            {{ field.access.write_action ?? "" }}</span
          >
        </article>
      </div>
    </section>
    <ProvenanceList :items="provenance" />
  </div>
  <p v-else class="page-loading">Loading register revision…</p>
</template>
